import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { Plus, Edit, Trash2, Save, X } from 'lucide-react';
import { 
  getDataInsertions, 
  getInvestmentProducts,
  createDataInsertion,
  updateDataInsertion,
  deleteDataInsertion,
  DataInsertion,
  InvestmentProduct,
  DataInsertionsResponse
} from '../services/investmentService';

const InsertData: React.FC = () => {
  const [editingInsertion, setEditingInsertion] = useState<DataInsertion | null>(null);
  const [showForm, setShowForm] = useState(false);

  const queryClient = useQueryClient();

  // Queries
  const { data: insertionsData, isLoading: insertionsLoading } = useQuery('data-insertions', getDataInsertions);
  const { data: products } = useQuery('investment-products', getInvestmentProducts);

  // Mutations
  const createInsertionMutation = useMutation(createDataInsertion, {
    onSuccess: () => {
      queryClient.invalidateQueries('data-insertions');
      setShowForm(false);
    },
  });

  const updateInsertionMutation = useMutation(
    ({ id, data }: { id: number; data: Partial<DataInsertion> }) => updateDataInsertion(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('data-insertions');
        setEditingInsertion(null);
      },
    }
  );

  const deleteInsertionMutation = useMutation(deleteDataInsertion, {
    onSuccess: () => {
      queryClient.invalidateQueries('data-insertions');
    },
  });

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const data = {
      product_id: parseInt(formData.get('product_id') as string),
      amount: parseFloat(formData.get('amount') as string),
      insertion_date: formData.get('insertion_date') as string,
      notes: formData.get('notes') as string,
    };

    if (editingInsertion) {
      updateInsertionMutation.mutate({ id: editingInsertion.id, data });
    } else {
      createInsertionMutation.mutate(data);
    }
  };

  const formatCurrency = (amount: number, currency: string) => {
    // Mapear códigos de moeda não padrão para códigos ISO 4217 válidos
    const currencyMap: Record<string, string> = {
      'USDT': 'USD', // Tether USD -> USD
      'BTC': 'USD',  // Bitcoin -> USD (para formatação)
      'ETH': 'USD',  // Ethereum -> USD (para formatação)
      'EUR': 'EUR',
      'USD': 'USD',
      'GBP': 'GBP',
    };

    const validCurrency = currencyMap[currency] || 'EUR';
    
    try {
      return new Intl.NumberFormat('pt-PT', {
        style: 'currency',
        currency: validCurrency,
      }).format(amount);
    } catch (error) {
      // Fallback se ainda houver erro
      return `${amount.toFixed(2)} ${currency}`;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Inserir Dados</h1>
        <p className="text-gray-600">Registe os seus investimentos por produto</p>
      </div>

      {/* Add Button */}
      <div className="flex justify-end">
        <button
          onClick={() => setShowForm(true)}
          className="btn btn-primary"
        >
          <Plus className="h-4 w-4 mr-2" />
          Novo Investimento
        </button>
      </div>

      {/* Latest Insertions by Product - Highlighted */}
      {insertionsData?.latest_by_product && insertionsData.latest_by_product.length > 0 && (
        <div className="card">
          <div className="mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Últimas Leituras por Produto</h2>
            <p className="text-sm text-gray-600">Leituras mais recentes de cada produto de investimento</p>
          </div>
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Área</th>
                  <th>Produto</th>
                  <th>Montante</th>
                  <th>Notas</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {insertionsData.latest_by_product
                  .sort((a, b) => {
                    // Primeiro ordena por área (area_name)
                    const areaComparison = a.area_name.localeCompare(b.area_name, 'pt-PT');
                    if (areaComparison !== 0) {
                      return areaComparison;
                    }
                    // Se as áreas são iguais, ordena por produto (product_name)
                    return a.product_name.localeCompare(b.product_name, 'pt-PT');
                  })
                  .map((insertion) => (
                  <tr key={insertion.id} className="bg-blue-50 border-l-4 border-blue-400">
                    <td className="font-medium">{new Date(insertion.insertion_date).toLocaleDateString('pt-PT')}</td>
                    <td className="font-medium">{insertion.area_name}</td>
                    <td className="font-medium">{insertion.product_name}</td>
                    <td className="font-medium">{formatCurrency(insertion.amount, insertion.currency_code)}</td>
                    <td>{insertion.notes || '-'}</td>
                    <td>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => setEditingInsertion(insertion)}
                          className="text-primary-600 hover:text-primary-900"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => deleteInsertionMutation.mutate(insertion.id)}
                          className="text-danger-600 hover:text-danger-900"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Recent Insertions Table */}
      <div className="card">
        <div className="mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Últimas 20 Leituras</h2>
          <p className="text-sm text-gray-600">Histórico das leituras mais recentes ordenadas por data</p>
        </div>
        <div className="overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th>Data</th>
                <th>Área</th>
                <th>Produto</th>
                <th>Montante</th>
                <th>Notas</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {insertionsLoading ? (
                <tr>
                  <td colSpan={6} className="text-center py-4">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600 mx-auto"></div>
                  </td>
                </tr>
              ) : insertionsData?.recent_insertions && insertionsData.recent_insertions.length > 0 ? (
                insertionsData.recent_insertions.map((insertion) => (
                  <tr key={insertion.id}>
                    <td>{new Date(insertion.insertion_date).toLocaleDateString('pt-PT')}</td>
                    <td className="font-medium">{insertion.area_name}</td>
                    <td>{insertion.product_name}</td>
                    <td>{formatCurrency(insertion.amount, insertion.currency_code)}</td>
                    <td>{insertion.notes || '-'}</td>
                    <td>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => setEditingInsertion(insertion)}
                          className="text-primary-600 hover:text-primary-900"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => deleteInsertionMutation.mutate(insertion.id)}
                          className="text-danger-600 hover:text-danger-900"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="text-center py-4 text-gray-500">
                    Nenhuma leitura encontrada
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Form Modal */}
      {(showForm || editingInsertion) && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                {editingInsertion ? 'Editar Investimento' : 'Novo Investimento'}
              </h3>
              <button
                onClick={() => {
                  setShowForm(false);
                  setEditingInsertion(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Produto
                </label>
                <select name="product_id" className="input" required>
                  <option value="">Selecionar produto</option>
                  {products?.map((product) => (
                    <option
                      key={product.id}
                      value={product.id}
                      selected={editingInsertion?.product_id === product.id}
                    >
                      {product.area_name} - {product.name} ({product.currency_code})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Montante
                </label>
                <input
                  type="number"
                  name="amount"
                  step="0.01"
                  min="0"
                  defaultValue={editingInsertion?.amount || ''}
                  className="input"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Data
                </label>
                <input
                  type="datetime-local"
                  name="insertion_date"
                  defaultValue={editingInsertion?.insertion_date ? 
                    new Date(editingInsertion.insertion_date).toISOString().slice(0, 16) : 
                    new Date().toISOString().slice(0, 16)
                  }
                  className="input"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notas (opcional)
                </label>
                <textarea
                  name="notes"
                  rows={3}
                  defaultValue={editingInsertion?.notes || ''}
                  className="input"
                />
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    setEditingInsertion(null);
                  }}
                  className="btn btn-secondary"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={createInsertionMutation.isLoading || updateInsertionMutation.isLoading}
                >
                  <Save className="h-4 w-4 mr-2" />
                  {editingInsertion ? 'Atualizar' : 'Criar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default InsertData;
